import { useEffect, useMemo, useRef, useState } from 'react'
import { Share, Text, View } from 'react-native'
import * as Location from 'expo-location'
import { useLocalSearchParams } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { EmptyState } from '@/components/ui/EmptyState'
import { ScreenContainer } from '@/components/ui/ScreenContainer'
import { ScreenHeader } from '@/components/ui/ScreenHeader'
import { Select, type SelectOption } from '@/components/ui/Select'
import { Skeleton } from '@/components/ui/Skeleton'
import { AlertTriangle, Clock, MapPin, Navigation, ShieldCheck, Users } from '@/components/ui/icons'
import { GuardianSetupModal, type GuardianSetupValues } from '@/components/tours/GuardianSetupModal'
import { TourRouteMap } from '@/components/tours/TourRouteMap'
import { TourTripTimeline } from '@/components/tours/TourTripTimeline'
import {
  useCreateGuardianShare,
  useRevokeGuardianShare,
  useTourTrip,
  useTourTripAction,
} from '@/lib/hooks/use-tours'
import { useTheme } from '@/lib/hooks/use-theme'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import { useTourLiveActivity } from '@/lib/hooks/use-tour-live-activity'
import { useSemanticColors } from '@/lib/theme/tokens'
import { FONTS, RADIUS, SPACING } from '@/lib/brand'
import { formatTourDeparture } from '@/lib/tours/format'
import { tourDetailTwoColumn } from '@/lib/tours/catalog-layout'
import { tourMapCanvasHeight } from '@/lib/maps/tour-map-layout'
import type { TourTripRunStatus } from '@/lib/types/tours.types'

export default function TourTripScreen() {
  const { bookingId = '' } = useLocalSearchParams<{ bookingId: string }>()
  const { t, i18n } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { isDesktop, isTablet, width } = useBreakpoint()
  const editorial = isDesktop || isTablet
  const mapHeight = tourMapCanvasHeight({
    editorial,
    twoColumn: tourDetailTwoColumn(editorial, width),
  })
  const [selectedStopId, setSelectedStopId] = useState<string | null>(null)
  const trip = useTourTrip(bookingId)
  const liveActivity = useTourLiveActivity(trip.data ?? null)
  const action = useTourTripAction(bookingId)
  const createGuardian = useCreateGuardianShare()
  const revokeGuardian = useRevokeGuardianShare(bookingId)
  const locationSubscription = useRef<Location.LocationSubscription | null>(null)
  const locationWriteInFlight = useRef(false)
  const [sharingLocation, setSharingLocation] = useState(false)
  const [guardianOpen, setGuardianOpen] = useState(false)
  const [emergencyConfirm, setEmergencyConfirm] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const driverOptions = useMemo<SelectOption[]>(
    () =>
      (trip.data?.operator?.availableDrivers ?? []).map((driver) => ({
        value: driver.id,
        label: driver.displayName,
        description: t('tours.trip.driver_license_expires', {
          date: new Date(`${driver.licenseExpiresAt}T12:00:00`).toLocaleDateString(i18n.language, {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          }),
        }),
        icon: <ShieldCheck size={18} color={colors.primary} />,
      })),
    [colors.primary, i18n.language, t, trip.data?.operator?.availableDrivers],
  )

  useEffect(
    () => () => {
      locationSubscription.current?.remove()
      locationSubscription.current = null
    },
    [],
  )

  const perform = async (
    operation: Parameters<typeof action.mutateAsync>[0]['action'],
    payload?: Record<string, unknown>,
  ) => {
    setErrorMessage(null)
    try {
      await action.mutateAsync({ action: operation, payload })
    } catch (error) {
      const code = (error as { code?: string }).code
      if (code === 'TOURS_DRIVER_REQUIRED') {
        setErrorMessage(t('tours.trip.driver_required'))
      } else if (code === 'TOURS_DRIVER_NOT_ELIGIBLE') {
        setErrorMessage(t('tours.trip.driver_not_eligible'))
      } else if (code === 'TOURS_DRIVER_BUSY') {
        setErrorMessage(t('tours.trip.driver_busy'))
      } else {
        setErrorMessage(t('tours.trip.action_error'))
      }
    }
  }

  const stopLocationSharing = () => {
    locationSubscription.current?.remove()
    locationSubscription.current = null
    setSharingLocation(false)
  }

  const startLocationSharing = async () => {
    setErrorMessage(null)
    const permission = await Location.requestForegroundPermissionsAsync()
    if (permission.status !== 'granted') {
      setErrorMessage(t('tours.trip.location_permission'))
      return
    }
    const subscription = await Location.watchPositionAsync(
      {
        accuracy: Location.Accuracy.High,
        timeInterval: 10_000,
        distanceInterval: 25,
      },
      (position) => {
        if (locationWriteInFlight.current) return
        locationWriteInFlight.current = true
        void action
          .mutateAsync({
            action: 'update_location',
            payload: {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
              accuracyM: position.coords.accuracy,
              headingDeg: position.coords.heading,
              speedMps: position.coords.speed,
              capturedAt: new Date(position.timestamp).toISOString(),
            },
          })
          .catch(() => setErrorMessage(t('tours.trip.location_error')))
          .finally(() => {
            locationWriteInFlight.current = false
          })
      },
    )
    locationSubscription.current?.remove()
    locationSubscription.current = subscription
    setSharingLocation(true)
  }

  const submitGuardian = async (values: GuardianSetupValues) => {
    setErrorMessage(null)
    try {
      const result = await createGuardian.mutateAsync({ bookingId, ...values })
      setGuardianOpen(false)
      await Share.share({
        title: t('tours.guardian.share_title'),
        message: t('tours.guardian.share_message', { url: result.shareUrl }),
        url: result.shareUrl,
      })
    } catch (error) {
      setErrorMessage(
        (error as { code?: string }).code === 'TOURS_GUARDIAN_ADULT_REQUIRED'
          ? t('tours.guardian.adult_required')
          : t('tours.guardian.create_error'),
      )
    }
  }

  if (trip.isLoading) {
    return (
      <ScreenContainer variant="dashboard">
        <ScreenHeader title={t('tours.trip.title')} fallbackHref="/(tabs)/tours/bookings" />
        <View style={{ paddingHorizontal: SPACING.lg, gap: SPACING.md }}>
          <Skeleton width="100%" height={96} borderRadius={RADIUS.xl} />
          <Skeleton width="100%" height={300} borderRadius={RADIUS.xl} staggerIndex={1} />
          <Skeleton width="100%" height={180} borderRadius={RADIUS.xl} staggerIndex={2} />
        </View>
      </ScreenContainer>
    )
  }

  if (trip.error || !trip.data) {
    return (
      <ScreenContainer variant="dashboard">
        <ScreenHeader title={t('tours.trip.title')} fallbackHref="/(tabs)/tours/bookings" />
        <View style={{ flex: 1, justifyContent: 'center' }}>
          <EmptyState
            illustration={<MapPin size={44} color={colors.textMuted} strokeWidth={1.5} />}
            title={t('tours.trip.not_found')}
            description={t('tours.trip.not_found_body')}
          />
        </View>
      </ScreenContainer>
    )
  }

  const data = trip.data
  const run = data.run
  const isOperator = data.viewerRole === 'operator'
  const currentPosition = run?.currentStopPosition ?? 0
  const nextStop = data.stops.find((stop) => stop.position === currentPosition + 1)
  const status = run?.status ?? 'scheduled'
  const emergencyStatus = run?.emergencyStatus ?? 'none'

  return (
    <ScreenContainer variant="dashboard">
      <ScreenHeader
        title={data.experience.title}
        subtitle={t('tours.trip.route', {
          origin: data.experience.originLabel,
          destination: data.experience.destinationLabel,
        })}
        fallbackHref={isOperator ? '/(tabs)/tours/host' : '/(tabs)/tours/bookings'}
      />
      <View
        style={{ paddingHorizontal: SPACING.lg, paddingBottom: SPACING['2xl'], gap: SPACING.md }}
      >
        <Card>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.md }}>
            <View
              style={{
                width: 44,
                height: 44,
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 22,
                backgroundColor: colors.surfaceSubtle,
              }}
            >
              <Navigation size={22} color={colors.primary} />
            </View>
            <View style={{ flex: 1 }}>
              <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 17, color: colors.textPrimary }}>
                {t(`tours.trip.status.${status}`)}
              </Text>
              <View style={{ marginTop: 3, flexDirection: 'row', alignItems: 'center', gap: 5 }}>
                <Clock size={14} color={colors.textMuted} />
                <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: colors.textSecondary }}>
                  {formatTourDeparture(data.departure.startsAt, i18n.language)}
                </Text>
              </View>
            </View>
            <View
              style={{
                paddingHorizontal: SPACING.sm,
                paddingVertical: 5,
                borderRadius: RADIUS.full,
                backgroundColor: colors.surfaceSubtle,
              }}
            >
              <Text
                style={{ fontFamily: FONTS.bodyBold, fontSize: 12, color: colors.textSecondary }}
              >
                {data.booking.passengerCount} {t('tours.trip.people_short')}
              </Text>
            </View>
          </View>
          {data.booking.startPoint ? (
            <View
              style={{
                marginTop: SPACING.sm,
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: 6,
              }}
              testID="tours.trip-start-point"
            >
              <MapPin size={15} color={colors.textAccent} strokeWidth={1.8} />
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text
                  style={{ fontFamily: FONTS.bodyBold, fontSize: 12, color: colors.textSecondary }}
                >
                  {t('tours.start_point.title')}
                </Text>
                <Text style={{ fontFamily: FONTS.body, fontSize: 13, color: colors.textPrimary }}>
                  {data.booking.startPoint.label}
                </Text>
                {data.booking.startPoint.address ? (
                  <Text style={{ fontFamily: FONTS.body, fontSize: 12, color: colors.textMuted }}>
                    {data.booking.startPoint.address}
                  </Text>
                ) : null}
              </View>
            </View>
          ) : null}
          {data.latestLocation ? (
            <Text
              style={{
                marginTop: SPACING.sm,
                fontFamily: FONTS.body,
                fontSize: 12,
                color: colors.textMuted,
              }}
            >
              {t('tours.trip.location_updated', {
                time: new Date(data.latestLocation.capturedAt).toLocaleTimeString(i18n.language, {
                  hour: 'numeric',
                  minute: '2-digit',
                }),
              })}
            </Text>
          ) : null}
          <View
            style={{ marginTop: SPACING.sm, flexDirection: 'row', alignItems: 'center', gap: 6 }}
          >
            <ShieldCheck size={15} color={colors.primary} />
            <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 12, color: colors.textSecondary }}>
              {t('tours.trip.operator_verified')}
            </Text>
          </View>
          {data.driver ? (
            <View
              style={{ marginTop: SPACING.sm, flexDirection: 'row', alignItems: 'center', gap: 6 }}
            >
              <ShieldCheck size={15} color={colors.primary} />
              <Text
                style={{
                  flex: 1,
                  fontFamily: FONTS.bodyBold,
                  fontSize: 12,
                  color: colors.textSecondary,
                }}
              >
                {t('tours.trip.driver_verified', { name: data.driver.displayName })}
              </Text>
            </View>
          ) : isOperator ? (
            <View
              style={{
                marginTop: SPACING.sm,
                flexDirection: 'row',
                alignItems: 'flex-start',
                gap: 6,
              }}
            >
              <AlertTriangle size={15} color={colors.textError} />
              <Text
                style={{
                  flex: 1,
                  fontFamily: FONTS.bodyBold,
                  fontSize: 12,
                  lineHeight: 17,
                  color: colors.textError,
                }}
              >
                {t('tours.trip.driver_required')}
              </Text>
            </View>
          ) : null}
        </Card>

        {emergencyStatus !== 'none' && emergencyStatus !== 'resolved' ? (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: SPACING.sm,
              padding: SPACING.md,
              borderRadius: RADIUS.lg,
              backgroundColor: `${colors.error}12`,
            }}
          >
            <AlertTriangle size={22} color={colors.textError} />
            <Text
              style={{ flex: 1, fontFamily: FONTS.bodyBold, fontSize: 14, color: colors.textError }}
            >
              {t(`tours.trip.emergency.${emergencyStatus}`)}
            </Text>
          </View>
        ) : null}

        <TourRouteMap
          stops={data.stops}
          latestLocation={data.latestLocation}
          routeGeojson={data.experience.routeGeojson}
          canvasHeight={mapHeight}
          selectedStopId={selectedStopId}
          onStopPress={setSelectedStopId}
          testID="tours.trip-map"
        />

        <Card>
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 17,
              color: colors.textPrimary,
              marginBottom: SPACING.md,
            }}
          >
            {t('tours.trip.itinerary')}
          </Text>
          <TourTripTimeline
            stops={data.stops}
            currentPosition={currentPosition}
            selectedStopId={selectedStopId}
            onStopPress={setSelectedStopId}
          />
        </Card>

        {isOperator && run ? (
          <Card>
            <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 17, color: colors.textPrimary }}>
              {t('tours.trip.operation')}
            </Text>
            <Text
              style={{
                marginTop: 4,
                fontFamily: FONTS.body,
                fontSize: 13,
                lineHeight: 19,
                color: colors.textSecondary,
              }}
            >
              {t('tours.trip.operation_body')}
            </Text>
            <View style={{ marginTop: SPACING.md, gap: SPACING.sm }}>
              {data.operator?.canAssignDriver ? (
                <View>
                  <Select
                    value={run.driverId}
                    onChange={(driverId) => void perform('assign_driver', { driverId })}
                    options={driverOptions}
                    label={t('tours.trip.driver_picker_label')}
                    placeholder={t('tours.trip.driver_picker_placeholder')}
                    sheetTitle={t('tours.trip.driver_picker_title')}
                    searchable={driverOptions.length > 6}
                    disabled={action.isPending || !['scheduled', 'boarding'].includes(run.status)}
                    testID="tours.driver-picker"
                  />
                  {driverOptions.length === 0 ? (
                    <Text
                      style={{
                        marginTop: -SPACING.sm,
                        marginBottom: SPACING.sm,
                        fontFamily: FONTS.body,
                        fontSize: 12,
                        lineHeight: 17,
                        color: colors.textSecondary,
                      }}
                    >
                      {t('tours.trip.driver_none_available')}
                    </Text>
                  ) : null}
                </View>
              ) : null}
              <OperatorActions
                status={run.status}
                loading={action.isPending}
                driverAssigned={Boolean(run.driverId)}
                onAction={(operation) => void perform(operation)}
              />
              {nextStop && ['boarding', 'in_progress', 'paused'].includes(run.status) ? (
                <Button
                  title={t('tours.trip.mark_next_stop', { stop: nextStop.label })}
                  variant="secondary"
                  onPress={() => void perform('advance_stop', { stopPosition: nextStop.position })}
                />
              ) : null}
              {['boarding', 'in_progress', 'paused', 'arrived'].includes(run.status) ? (
                <View style={{ gap: 6 }}>
                  <Button
                    title={
                      sharingLocation
                        ? t('tours.trip.stop_location')
                        : t('tours.trip.share_location')
                    }
                    variant="secondary"
                    icon={<MapPin size={18} />}
                    onPress={
                      sharingLocation ? stopLocationSharing : () => void startLocationSharing()
                    }
                  />
                  <Text
                    style={{
                      fontFamily: FONTS.body,
                      fontSize: 12,
                      lineHeight: 17,
                      color: colors.textSecondary,
                    }}
                  >
                    {t('tours.trip.location_foreground_hint')}
                  </Text>
                </View>
              ) : null}
              {run.emergencyStatus === 'triggered' ? (
                <Button
                  title={t('tours.trip.acknowledge_emergency')}
                  variant="secondary"
                  onPress={() => void perform('acknowledge_emergency')}
                />
              ) : run.emergencyStatus === 'acknowledged' ? (
                <Button
                  title={t('tours.trip.resolve_emergency')}
                  variant="secondary"
                  onPress={() => void perform('resolve_emergency')}
                />
              ) : null}
            </View>
          </Card>
        ) : null}

        {!isOperator && ['paid', 'in_progress'].includes(data.booking.status) ? (
          <Card>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: SPACING.sm }}>
              <ShieldCheck size={22} color={colors.primary} />
              <Text
                style={{
                  flex: 1,
                  fontFamily: FONTS.bodyBold,
                  fontSize: 17,
                  color: colors.textPrimary,
                }}
              >
                {t('tours.guardian.title')}
              </Text>
            </View>
            <Text
              style={{
                marginTop: SPACING.sm,
                fontFamily: FONTS.body,
                fontSize: 13,
                lineHeight: 19,
                color: colors.textSecondary,
              }}
            >
              {data.guardian.activeShare
                ? t('tours.guardian.active', { name: data.guardian.activeShare.guardianName })
                : t('tours.guardian.body')}
            </Text>
            <View style={{ marginTop: SPACING.md, gap: SPACING.sm }}>
              <Button
                title={
                  data.guardian.activeShare
                    ? t('tours.guardian.replace_link')
                    : t('tours.guardian.setup')
                }
                variant="secondary"
                icon={<Users size={18} />}
                onPress={() => setGuardianOpen(true)}
              />
              {data.guardian.activeShare ? (
                <Button
                  title={t('tours.guardian.revoke')}
                  variant="text"
                  loading={revokeGuardian.isPending}
                  onPress={() => void revokeGuardian.mutateAsync()}
                />
              ) : null}
            </View>
          </Card>
        ) : null}

        {!isOperator && liveActivity.available && !liveActivity.active ? (
          <Button
            title={t('tours.trip.live_activity_enable')}
            variant="secondary"
            loading={liveActivity.isPending}
            onPress={() => {
              void liveActivity
                .activate()
                .catch(() => setErrorMessage(t('tours.trip.action_error')))
            }}
          />
        ) : null}

        {!isOperator && run && ['boarding', 'in_progress', 'paused'].includes(run.status) ? (
          <Button
            title={t('tours.trip.emergency_button')}
            variant="secondary"
            icon={<AlertTriangle size={18} />}
            onPress={() => setEmergencyConfirm(true)}
            testID="tours.trip-emergency"
          />
        ) : null}

        {errorMessage ? (
          <Text
            accessibilityRole="alert"
            style={{
              fontFamily: FONTS.body,
              fontSize: 13,
              color: colors.textError,
              textAlign: 'center',
            }}
          >
            {errorMessage}
          </Text>
        ) : null}
      </View>

      <GuardianSetupModal
        visible={guardianOpen}
        loading={createGuardian.isPending}
        onClose={() => setGuardianOpen(false)}
        onSubmit={(values) => void submitGuardian(values)}
      />
      <ConfirmDialog
        visible={emergencyConfirm}
        title={t('tours.trip.emergency_confirm_title')}
        message={t('tours.trip.emergency_confirm_body')}
        confirmLabel={t('tours.trip.emergency_confirm_yes')}
        cancelLabel={t('common.cancel')}
        destructive
        onCancel={() => setEmergencyConfirm(false)}
        onConfirm={() => {
          setEmergencyConfirm(false)
          void perform('trigger_emergency')
        }}
      />
    </ScreenContainer>
  )
}

function OperatorActions({
  status,
  loading,
  driverAssigned,
  onAction,
}: {
  status: TourTripRunStatus
  loading: boolean
  driverAssigned: boolean
  onAction: (action: 'set_boarding' | 'start' | 'pause' | 'resume' | 'arrive' | 'complete') => void
}) {
  const { t } = useTranslation()
  if (status === 'scheduled') {
    return (
      <Button
        title={t('tours.trip.start_boarding')}
        loading={loading}
        disabled={!driverAssigned}
        onPress={() => onAction('set_boarding')}
      />
    )
  }
  if (status === 'boarding') {
    return (
      <Button
        title={t('tours.trip.start_trip')}
        loading={loading}
        disabled={!driverAssigned}
        onPress={() => onAction('start')}
      />
    )
  }
  if (status === 'in_progress') {
    return (
      <View style={{ gap: SPACING.sm }}>
        <Button
          title={t('tours.trip.mark_arrival')}
          loading={loading}
          onPress={() => onAction('arrive')}
        />
        <Button
          title={t('tours.trip.pause')}
          variant="secondary"
          onPress={() => onAction('pause')}
        />
      </View>
    )
  }
  if (status === 'paused') {
    return (
      <Button title={t('tours.trip.resume')} loading={loading} onPress={() => onAction('resume')} />
    )
  }
  if (status === 'arrived') {
    return (
      <Button
        title={t('tours.trip.complete')}
        loading={loading}
        onPress={() => onAction('complete')}
      />
    )
  }
  return null
}
